import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, X, Compass, BookOpen } from "lucide-react";
import { api } from "@/lib/api";
import { HELP } from "@/components/HelpTip";

const KEY = "bf_onboarding_dismissed";

export default function GettingStarted({ data }) {
  const [hidden, setHidden] = useState(() => localStorage.getItem(KEY) === "1");
  const [expanded, setExpanded] = useState(false);
  const [state, setState] = useState({ bills: 0, debts: 0, plan: false, incomes: 0, household: false });
  const [glossary, setGlossary] = useState(false);

  useEffect(() => {
    if (hidden) return;
    Promise.all([api.get("/bills"), api.get("/debts"), api.get("/payoff-plan"), api.get("/incomes"), api.get("/household")]).then(([b, d, p, i, h]) =>
      setState({ bills: b.data.length, debts: d.data.length, plan: !!p.data.created_at, incomes: i.data.length, household: !!h.data.household })
    ).catch(() => {});
  }, [hidden, data]);

  if (hidden) return null;
  const steps = [
    { done: state.bills > 0, to: "/bills", title: "Add your bills", body: "Rent, utilities, subscriptions — anything with a due date. Tag autopay ones so reminders stay quiet." },
    { done: state.debts > 0, to: "/debts?coach=1", title: "Add credit cards & loans", body: "Balance, APR, minimum payment and credit limit. $0 cards count too — they lower utilization." },
    { done: state.plan, to: "/debts", title: "Pick Snowball or Avalanche", body: "Then set your monthly snowball — the extra you can put toward debt. We'll tell you exactly what to pay." },
    { done: state.incomes > 0, to: "/cashflow", title: "Add your income", body: "So the forecast can flag tight months before they happen." },
    { done: state.household, to: "/household", title: "Invite your partner (optional)", body: "Share one plan, one calendar, one set of bills." },
  ];
  const doneCount = steps.filter(s => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const dismiss = () => { localStorage.setItem(KEY, "1"); setHidden(true); };
  const next = steps.find(s => !s.done);

  // Once the user has real momentum (≥2 steps done), collapse to a slim pill by default.
  const collapsed = doneCount >= 2 && !expanded;

  if (collapsed) {
    return (
      <div className="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-teal-500/25 bg-teal-500/5" data-testid="getting-started-collapsed">
        <Compass className="h-4 w-4 text-teal-500 shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wider text-teal-500 shrink-0">Getting started · {doneCount}/{steps.length}</span>
        <div className="h-1.5 flex-1 min-w-[60px] rounded-full bg-accent overflow-hidden"><div className="h-full bg-teal-500 transition-[width]" style={{ width: `${pct}%` }} /></div>
        <button onClick={() => setExpanded(true)} data-testid="getting-started-expand" className="text-xs font-semibold text-teal-500 hover:underline shrink-0">
          {next ? `Next: ${next.title}` : "Review steps"}
        </button>
        <button onClick={dismiss} className="p-1 rounded-md hover:bg-accent text-muted-foreground shrink-0" title="Hide" data-testid="getting-started-dismiss"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }

  return (
    <div className="p-5 rounded-2xl border border-teal-500/30 bg-teal-500/5 relative" data-testid="getting-started">
      <button onClick={dismiss} className="absolute right-3 top-3 p-1 rounded-md hover:bg-accent text-muted-foreground" title="Hide" data-testid="getting-started-dismiss"><X className="h-4 w-4" /></button>
      {doneCount >= 2 && (
        <button onClick={() => setExpanded(false)} data-testid="getting-started-collapse" className="absolute right-11 top-3 px-2 py-1 rounded-md hover:bg-accent text-muted-foreground text-[11px] font-semibold">Collapse</button>
      )}
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-teal-500 font-semibold"><Compass className="h-3.5 w-3.5" /> Getting started · {doneCount}/{steps.length}</div>
      <div className="font-display font-bold text-lg mt-1">New here? Five steps to your first payoff plan</div>
      <div className="mt-1 h-1.5 rounded-full bg-accent overflow-hidden"><div className="h-full bg-teal-500 transition-[width]" style={{ width: `${pct}%` }} /></div>
      <ol className="mt-4 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {steps.map((s, i) => (
          <li key={s.title}>
            <Link to={s.to} data-testid={`getting-started-step-${i + 1}`} className={`block h-full p-3 rounded-xl border transition hover:-translate-y-0.5 ${s.done ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-card hover:border-teal-500/40"}`}>
              <div className="flex items-center gap-2 text-sm font-semibold">{s.done ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-muted-foreground" />}{s.title}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{s.body}</div>
            </Link>
          </li>
        ))}
      </ol>
      <button onClick={() => setGlossary(g => !g)} data-testid="glossary-toggle" className="mt-4 inline-flex items-center gap-1.5 text-xs text-teal-500 hover:underline"><BookOpen className="h-3.5 w-3.5" /> {glossary ? "Hide" : "New to personal finance? Read the 2-minute glossary"}</button>
      {glossary && (
        <dl className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-xs" data-testid="glossary">
          {["apr", "min_payment", "snowball", "avalanche", "snowball_amount", "utilization", "statement_day", "promo_apr", "freed_minimums", "cash_flow"].map(k => (
            <div key={k} className="p-2 rounded-lg bg-card border border-border/60"><dt className="font-semibold text-teal-500">{HELP[k][0]}</dt><dd className="text-muted-foreground mt-0.5">{HELP[k][1]}</dd></div>
          ))}
        </dl>
      )}
    </div>
  );
}
